import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '@/stores/authStore';

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || '';

class SocketService {
  private socket: Socket | null = null;
  private listeners: Map<string, Set<(data: any) => void>> = new Map();

  connect(): void {
    if (this.socket?.connected) return;

    const { accessToken } = useAuthStore.getState();

    this.socket = io(SOCKET_URL, {
      auth: { token: accessToken },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    this.socket.on('connect', () => {
      console.log('Socket connected');
    });

    this.socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
    });

    this.socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
    });

    // Re-emit events to registered listeners
    this.socket.onAny((event, data) => {
      const eventListeners = this.listeners.get(event);
      if (eventListeners) {
        eventListeners.forEach((callback) => callback(data));
      }
    });
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  on(event: string, callback: (data: any) => void): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);

    // Return unsubscribe function
    return () => {
      this.listeners.get(event)?.delete(callback);
    };
  }

  off(event: string, callback?: (data: any) => void): void {
    if (callback) {
      this.listeners.get(event)?.delete(callback);
    } else {
      this.listeners.delete(event);
    }
  }

  emit(event: string, data?: any): void {
    if (this.socket?.connected) {
      this.socket.emit(event, data);
    }
  }

  joinOrganization(organizationId: string): void {
    this.emit('join:organization', organizationId);
  }

  trackVehicle(vehicleId: string): void {
    this.emit('track:vehicle', vehicleId);
  }

  untrackVehicle(vehicleId: string): void {
    this.emit('untrack:vehicle', vehicleId);
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }
}

export const socketService = new SocketService();

// React hook for socket events
import { useEffect, useState } from 'react';

export function useSocket() {
  const [isConnected, setIsConnected] = useState(false);
  const { isAuthenticated } = useAuthStore();

  useEffect(() => {
    if (isAuthenticated) {
      socketService.connect();
      setIsConnected(socketService.isConnected());

      const unsubConnect = socketService.on('connect', () => {
        setIsConnected(true);
      });

      const unsubDisconnect = socketService.on('disconnect', () => {
        setIsConnected(false);
      });

      return () => {
        unsubConnect();
        unsubDisconnect();
      };
    } else {
      socketService.disconnect();
      setIsConnected(false);
    }
  }, [isAuthenticated]);

  return { isConnected, socket: socketService };
}

export function useSocketEvent<T>(event: string, callback: (data: T) => void) {
  useEffect(() => {
    const unsubscribe = socketService.on(event, callback);
    return unsubscribe;
  }, [event, callback]);
}
